import { useEffect, useState, useCallback } from 'react';
import { LuArrowLeft, LuSearch, LuX, LuArrowRight, LuAlertCircle } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import community_api from '../../api-services/community/community.services';
import { useSearchCommunities } from '../../api-services/contents/useSearchCommunities';
import { SITELINK } from '../../app-routes/Links';
import { Community } from '../../app-schemas/community/community.interface';
import { truncateText } from '../../app-utils';
import { CommunityImage } from '../../assets/custom-icons';
import { useTheme } from '../../context/theme-context';

// Skeleton Components
const SearchSkeleton = () => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className={`p-3 rounded-lg flex gap-1 justify-between items-center mb-2 animate-pulse ${
            isDarkMode ? 'bg-neutral-800' : 'bg-white'
          }`}
        >
          <div className="flex-grow flex">
            <div className="w-[50px] mt-1">
              <div className="w-[40px] h-[40px] rounded-full bg-gray-200 dark:bg-gray-700" />
            </div>
            <div className="px-3 w-full py-2">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24" />
            </div>
          </div>
          <div className="flex-grow flex justify-end">
            <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
          </div>
        </div>
      ))}
    </div>
  );
};

// Error Component
const SearchError = ({ onRetry, error }: { onRetry: () => void; error: any }) => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className={`flex flex-col items-center justify-center min-h-[60vh] p-6`}>
      <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
        isDarkMode ? 'bg-red-900/30' : 'bg-red-100'
      }`}>
        <LuAlertCircle className={`w-10 h-10 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
      </div>
      <h3 className={`text-lg font-semibold mb-2 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        Failed to search
      </h3>
      <p className={`text-sm mb-6 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        {error?.message || 'Unable to perform search. Please check your connection.'}
      </p>
      <button
        onClick={onRetry}
        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        Try Again
      </button>
    </div>
  );
};

// Empty State Component
const SearchEmpty = ({ searchTerm }: { searchTerm: string }) => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className={`flex flex-col items-center justify-center min-h-[60vh] p-6`}>
      <LuSearch className={`w-20 h-20 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
      <h3 className={`text-lg font-semibold mb-2 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        No results found
      </h3>
      <p className={`text-sm text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        {searchTerm ? (
          <>We couldn't find any communities matching "<span className="font-medium">{searchTerm}</span>"</>
        ) : (
          <>Search for communities, people, posts, and more</>
        )}
      </p>
      <p className={`text-xs mt-2 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        Try different keywords or browse our recommended communities
      </p>
    </div>
  );
};

// Initial Empty State (when no search has been performed)
const InitialEmptyState = () => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className={`flex flex-col items-center justify-center min-h-[60vh] p-6`}>
      <LuSearch className={`w-20 h-20 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
      <h3 className={`text-lg font-semibold mb-2 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        Search Communities
      </h3>
      <p className={`text-sm text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Find communities that match your interests
      </p>
      <p className={`text-xs mt-2 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        Type at least 3 characters to start searching
      </p>
    </div>
  );
};

// main-component-ui
const Search = () => {
  document.body.classList.remove('overflow-hidden');
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const searchHook = useSearchCommunities();
  const [filterBy, setFilterBy] = useState('Communities');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchResult, setSearchResult] = useState<any>([]);
  const [searchResultLoading, setSearchResultLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<any>(null);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  // Define searchItem with useCallback
  const searchItem = useCallback(async () => {
    if (debouncedSearch.length < 3) return;

    setSearchResultLoading(true);
    setIsError(false);

    try {
      await searchHook.mutate(debouncedSearch, {
        onSuccess: (data) => {
          setSearchResult(data);
          setSearchResultLoading(false);
        },
        onError: (err) => {
          console.error('Error searching:', err);
          setIsError(true);
          setError(err);
          setSearchResultLoading(false);
        },
      });
    } catch (err) {
      console.error('Error searching:', err);
      setIsError(true);
      setError(err);
      setSearchResultLoading(false);
    }
  }, [debouncedSearch, searchHook]);

  // Trigger search when debouncedSearch changes
  useEffect(() => {
    if (debouncedSearch.length > 2) {
      searchItem();
    } else {
      setSearchResult([]);
      setIsError(false);
      setError(null);
    }
  }, [debouncedSearch, searchItem]);

  const handleRetry = () => {
    if (debouncedSearch.length > 2) {
      searchItem();
    }
  };

  const handleGoBack = () => {
    navigate(-1); // Navigate to previous page
  };

  /* Join community function */
  const handleJoinCommunity = async (id: number) => {
    try {
      const res = await community_api.JoinCommunity(id);
      if (res?.data?.status) {
        navigate(SITELINK.COMMUNITY_ACTIVITIES + '/' + id);
      }
    } catch (error) {
      console.error('Error joining community:', error);
    }
  };

  // Get the search results based on filter
  const getFilteredResults = () => {
    if (!searchResult?.data) return [];
    
    switch (filterBy) {
      case 'Communities':
        return searchResult.data;
      case 'People':
      case 'Posts':
      case 'Market':
      case 'Rides':
      case 'Jobs':
        return [];
      default:
        return searchResult.data;
    }
  };

  const filteredResults = getFilteredResults();
  const hasSearchTerm = debouncedSearch.length > 2;

  return (
    <section className={`min-h-screen ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
      {/* top lead area and search area */}
      <div className="fixed top-0 w-full z-50 shadow-sm">
        <div className={`relative px-2 py-3 ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
          {/* go back button */}
          <button
            onClick={handleGoBack}
            className="absolute top-3 left-1 font-semibold text-sm flex p-2.5 px-4 hover:opacity-70 transition-opacity"
            aria-label="Go back"
          >
            <LuArrowLeft className={`text-2xl ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
          </button>

          {/* search input */}
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus={true}
            type="text"
            placeholder="Search communities, people, posts..."
            className={`text-sm p-3 pl-[45px] pr-[50px] px-4 w-full font-[500] border rounded-lg focus:outline-none focus:ring-2 transition-all ${
              isDarkMode 
                ? 'bg-neutral-700 border-neutral-600 text-gray-200 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500' 
                : 'border-gray-300 text-gray-600 placeholder-gray-400 focus:ring-blue-400 focus:border-blue-400'
            }`}
          />

          {/* search icon && search cancel */}
          {search.length < 2 ? (
            <span className={`absolute top-[1rem] right-4 px-1 py-2 font-medium`}>
              <LuSearch className={`text-xl ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
            </span>
          ) : (
            <button
              onClick={() => setSearch('')}
              type="button"
              className={`absolute top-[1rem] right-4 px-1 py-2 text-sm font-medium`}
              aria-label="Clear search"
            >
              <LuX className={`text-xl ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            </button>
          )}
        </div>

        {/* Filter Tabs */}
        <div className={`search-item-filter border-b shadow-sm flex gap-6 text-xs font-medium overflow-x-auto px-3 ${
          isDarkMode ? 'bg-neutral-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          {['Communities', 'People', 'Posts', 'Market', 'Rides', 'Jobs'].map((filter) => (
            <button
              key={filter}
              onClick={() => setFilterBy(filter)}
              className={`py-2 whitespace-nowrap transition-all duration-200 ${
                filterBy === filter
                  ? `text-custom-600 border-b-2 border-custom-600 ${isDarkMode ? 'text-blue-400 border-blue-400' : 'text-custom-600'}`
                  : `${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
              }`}
            >
              {filter}
            </button>
          ))}
        </div>
      </div>

      {/* search results */}
      <div className="relative top-[110px] py-5 px-3 pb-24">
        <div className="flex flex-col w-full mt-2 pb-2">
          {/* Loading State */}
          {searchResultLoading && (
            <SearchSkeleton />
          )}

          {/* Error State */}
          {isError && !searchResultLoading && (
            <SearchError onRetry={handleRetry} error={error} />
          )}

          {/* Results State */}
          {!searchResultLoading && !isError && hasSearchTerm && filteredResults.length > 0 && (
            <>
              {/* Results Header */}
              <div className={`mb-4 pb-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Found {filteredResults.length} {filterBy.toLowerCase().slice(0, -1)} 
                  {filteredResults.length !== 1 ? 'ies' : 'y'} for "{debouncedSearch}"
                </p>
              </div>

              {/* Results List */}
              {filteredResults.map((community: Community, index: number) => (
                <div
                  key={community.id || index}
                  className={`p-3 rounded-lg gap-1 justify-between items-center flex mb-3 transition-all duration-200 hover:shadow-md ${
                    isDarkMode 
                      ? 'bg-neutral-800 border border-gray-700 hover:bg-neutral-700' 
                      : 'bg-white border border-gray-100 shadow-sm hover:shadow-md'
                  }`}
                >
                  <div className="flex-grow flex">
                    <div className="overflow-hidden flex justify-center w-[50px] mt-1">
                      <img
                        src={community.cover_image_url || CommunityImage}
                        alt={community.name}
                        className="object-cover rounded-full border border-gray-100 dark:border-gray-600 w-[45px] h-[45px] shadow-sm overflow-hidden"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = CommunityImage;
                        }}
                      />
                    </div>

                    <div className="px-3 w-full text-xs py-1">
                      <h3 className={`font-semibold mb-0 text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {truncateText(community.name, 30)}
                      </h3>

                      <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {community.city && community.country 
                          ? `${community.city}, ${community.country}`
                          : community.city || community.country || 'Community'}
                      </p>

                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {community.members} member{community.members !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  <div className="flex-grow flex justify-end">
                    <button
                      onClick={() => handleJoinCommunity(community.id)}
                      className={`p-2 rounded-full transition-all duration-200 hover:scale-110 ${
                        isDarkMode 
                          ? 'bg-neutral-700 hover:bg-neutral-600 text-blue-400' 
                          : 'bg-gray-100 hover:bg-gray-200 text-custom-600'
                      }`}
                      aria-label={`Join ${community.name}`}
                    >
                      <LuArrowRight className='text-xl font-bold' />
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Empty State - No results for search */}
          {!searchResultLoading && !isError && hasSearchTerm && filteredResults.length === 0 && (
            <SearchEmpty searchTerm={debouncedSearch} />
          )}

          {/* Initial State - No search performed yet */}
          {!searchResultLoading && !isError && !hasSearchTerm && (
            <InitialEmptyState />
          )}

          {/* Placeholder for unimplemented filters */}
          {!searchResultLoading && !isError && hasSearchTerm && filterBy !== 'Communities' && (
            <div className={`text-center py-16 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <LuSearch className="w-16 h-16 mx-auto mb-3 opacity-50" />
              <p className="text-sm">{filterBy} search coming soon!</p>
              <p className="text-xs mt-1">We're working on adding {filterBy.toLowerCase()} search functionality.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Search;